import {
  PermissionAction,
  PermissionModule,
  type Permission,
} from "@ibada/shared-types/authorization"
import { RoleScope, RoleStatus } from "@ibada/shared-types/workspace"

import { http } from "@/shared/lib/api/http"
import { extractWorkspaceSlugFromAdminPath } from "@/shared/lib/branch/branch-admin-routing"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"

/** A module + the actions it exposes, from GET /roles/permissions (the role-editor catalog). */
export type RolePermissionModuleCatalog = {
  module: PermissionModule
  actions: PermissionAction[]
}

export type RolePermissionsCatalog = {
  modules: RolePermissionModuleCatalog[]
  permissions: Permission[]
}

/** A workspace custom role (named permission bundle). */
export type WorkspaceCustomRole = {
  id: string
  workspaceId: string | null
  key: string
  name: string
  description: string | null
  scope: RoleScope
  status: RoleStatus
  permissions: Permission[]
  /** Distinct users assigned this role (present on list responses only). */
  assignedUserCount?: number
  createdAt: string
  updatedAt: string
}

export type ListWorkspaceRolesResponse = {
  items: WorkspaceCustomRole[]
  total: number
  page: number
  limit: number
}

/** A user assigned a role (from GET /roles/:id/users) — the same set the role's `assignedUserCount` counts. */
export type RoleUser = {
  id: string
  firstName: string
  lastName: string
  email: string
  avatarUrl: string | null
  /** Branch(es) the user holds this role on. */
  branches: Array<{ id: string; name: string; slug: string }>
}

export type ListWorkspaceRolesParams = {
  status?: RoleStatus
  scope?: RoleScope
  search?: string
  page?: number
  limit?: number
  /** Branch receiving an assignment. This asks the API for assignment-read authority, not roles.manage. */
  assignmentBranchId?: string
}

export type CreateWorkspaceRoleInput = {
  name: string
  description?: string
  scope?: RoleScope
  status?: RoleStatus
  permissions: Permission[]
}

export type UpdateWorkspaceRoleInput = {
  name?: string
  description?: string
  status?: RoleStatus
  permissions?: Permission[]
}

/**
 * Roles are workspace-level and the management UI lives on a workspace settings route (no branch in the URL),
 * so resolve the active workspace slug from the URL (store fallback) and send x-workspace-slug explicitly;
 * the backend resolves the workspace from it (no branch needed for workspace-level roles).
 */
function workspaceHeaders(): Record<string, string> {
  const slug =
    (typeof window !== "undefined" ? extractWorkspaceSlugFromAdminPath(window.location.pathname) : null) ??
    useAuthContextStore.getState().activeWorkspaceSlug
  return slug ? { "x-workspace-slug": slug } : {}
}

/**
 * Roles are pickers, not a paged table — every caller renders the whole set to choose from.
 *
 * Without an explicit limit the server applied its default of 20, so a workspace past twenty roles offered
 * a silently truncated list in the role assignment editors and the branch role multi-select: the role you
 * wanted simply was not there, with nothing to say it existed. 200 is the server's ceiling (MAX_LIMIT).
 */
async function listRoles(params?: ListWorkspaceRolesParams): Promise<ListWorkspaceRolesResponse> {
  const response = await http.get<ListWorkspaceRolesResponse>("/roles", {
    // `?? 200` rather than a spread default: a caller passing an explicit `limit: undefined` would override
    // the default with nothing and fall back to the server's 20, which is the bug this line exists to stop.
    params: { ...params, limit: params?.limit ?? 200 },
    headers: workspaceHeaders(),
  })
  return response.data
}

async function getPermissionsCatalog(): Promise<RolePermissionsCatalog> {
  const response = await http.get<RolePermissionsCatalog>("/roles/permissions", { headers: workspaceHeaders() })
  return response.data
}

async function getRole(id: string): Promise<WorkspaceCustomRole> {
  const response = await http.get<WorkspaceCustomRole>(`/roles/${id}`, { headers: workspaceHeaders() })
  return response.data
}

async function listRoleUsers(roleId: string): Promise<RoleUser[]> {
  const response = await http.get<{ items: RoleUser[] }>(`/roles/${roleId}/users`, { headers: workspaceHeaders() })
  return response.data.items
}

async function createRole(input: CreateWorkspaceRoleInput): Promise<WorkspaceCustomRole> {
  const response = await http.post<WorkspaceCustomRole>("/roles", input, { headers: workspaceHeaders() })
  return response.data
}

async function updateRole(id: string, input: UpdateWorkspaceRoleInput): Promise<WorkspaceCustomRole> {
  const response = await http.patch<WorkspaceCustomRole>(`/roles/${id}`, input, { headers: workspaceHeaders() })
  return response.data
}

async function deleteRole(id: string): Promise<{ id: string }> {
  const response = await http.delete<{ id: string }>(`/roles/${id}`, { headers: workspaceHeaders() })
  return response.data
}

export const roleManagementApi = {
  listRoles,
  getPermissionsCatalog,
  getRole,
  listRoleUsers,
  createRole,
  updateRole,
  deleteRole,
}
